import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError, combineLatest, from, concatMap, map, tap, take, toArray, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  Document,
  DocumentStatus,
  DocumentSummary,
  Employee,
  NotificationRecord,
  NotificationSettings,
  PersonalEvent,
  NotificationType,
  NotificationChannel,
  CVAnalysisStatus,
  DocumentType,
  PersonalEventType
} from '../../models/hr-models';

@Injectable({
  providedIn: 'root'
})
export class HrService {
  // Use the environment configuration for API URL
  private apiUrl = environment.apiUrl;
  private documentSubject = new BehaviorSubject<Document[]>([]);
  private employeeSubject = new BehaviorSubject<Employee[]>([]);
  private personalEventsSubject = new BehaviorSubject<PersonalEvent[]>([]);
  private notificationSettingsSubject = new BehaviorSubject<NotificationSettings>({
    documentExpiryReminderDays: [30, 15, 7, 1],
    birthdayReminderDays: 3,
    anniversaryReminderDays: 5,
    defaultNotificationChannel: NotificationChannel.Both,
    hrEmailAddresses: ['hr@company.com']
  });
  private documentTypesSubject = new BehaviorSubject<DocumentType[]>([]);

  // Exposed as observables
  documents$ = this.documentSubject.asObservable();
  employees$ = this.employeeSubject.asObservable();
  personalEvents$ = this.personalEventsSubject.asObservable();
  notificationSettings$ = this.notificationSettingsSubject.asObservable();
  documentTypes$ = this.documentTypesSubject.asObservable();

  constructor(private http: HttpClient) {
    // Initialize by loading data
    this.refreshData();
  }

  // Data refresh method
  refreshData(): void {
    this.getEmployees().subscribe();
    this.getDocuments().subscribe();
    this.getPersonalEvents().subscribe();
    this.getNotificationSettings().subscribe();
    this.syncEmployeeDateEvents().subscribe(); // Add synchronization of employee dates
    this.getDocumentTypes().subscribe();
  }

  // Document methods
  getDocuments(): Observable<Document[]> {
    return this.http.get<any[]>(`${this.apiUrl}/documents`).pipe(
      map(documents => documents.map(doc => ({
        id: doc.id,
        employeeId: doc.employee_id,
        documentType: doc.document_type,
        title: doc.title,
        issueDate: new Date(doc.issue_date),
        expiryDate: new Date(doc.expiry_date),
        status: doc.status,
        notificationsSent: doc.notifications_sent || [],
        documentUrl: doc.document_url,
        notes: doc.notes,
        cv_summary: doc.cv_summary,
        cv_analysis_status: doc.cv_analysis_status,
        cv_analysis_date: doc.cv_analysis_date ? new Date(doc.cv_analysis_date) : undefined,
        file_name: doc.file_name,
        file_path: doc.file_path,
        file_type: doc.file_type,
        file_size: doc.file_size
      }))),
      tap(documents => this.documentSubject.next(documents)),
      catchError(this.handleError<Document[]>('getDocuments', []))
    );
  }

  getDocumentById(id: number): Observable<Document> {
    return this.http.get<any>(`${this.apiUrl}/documents/${id}`).pipe(
      map(doc => ({
        id: doc.id,
        employeeId: doc.employee_id,
        documentType: doc.document_type,
        title: doc.title,
        issueDate: new Date(doc.issue_date),
        expiryDate: new Date(doc.expiry_date),
        status: doc.status,
        notificationsSent: doc.notifications_sent || [],
        documentUrl: doc.document_url,
        notes: doc.notes,
        cv_summary: doc.cv_summary,
        cv_analysis_status: doc.cv_analysis_status,
        cv_analysis_date: doc.cv_analysis_date ? new Date(doc.cv_analysis_date) : undefined,
        file_name: doc.file_name,
        file_path: doc.file_path,
        file_type: doc.file_type,
        file_size: doc.file_size
      })),
      catchError(this.handleError<Document>(`getDocument id=${id}`))
    );
  }

  getDocumentsByEmployeeId(employeeId: number): Observable<Document[]> {
    return this.documents$.pipe(
      map(documents => documents.filter(doc => doc.employeeId === employeeId))
    );
  }

  addDocument(document: Document): Observable<Document> {
    console.log('[HR Service] Adding document', document);
    
    // Format dates in the correct format (YYYY-MM-DD) for the backend
    let issueDate: string | null = null;
    let expiryDate: string | null = null;
    
    // Format issue date (if present)
    if (document.issueDate) {
      if (document.issueDate instanceof Date) {
        issueDate = document.issueDate.toISOString().split('T')[0];
      } else {
        // If it's a string or some other format, try to convert it
        const issueDateObj = new Date(document.issueDate as any);
        if (!isNaN(issueDateObj.getTime())) {
          issueDate = issueDateObj.toISOString().split('T')[0];
        }
      }
    }
    
    // Format expiry date (if present)
    if (document.expiryDate) {
      if (document.expiryDate instanceof Date) {
        expiryDate = document.expiryDate.toISOString().split('T')[0];
      } else {
        // If it's a string or some other format, try to convert it
        const expiryDateObj = new Date(document.expiryDate as any);
        if (!isNaN(expiryDateObj.getTime())) {
          expiryDate = expiryDateObj.toISOString().split('T')[0];
        }
      }
    }
    
    console.log('[HR Service] Formatted issue date:', issueDate);
    console.log('[HR Service] Formatted expiry date:', expiryDate);
    
    // Convert to backend format with snake_case
    const backendDocument = {
      employee_id: document.employeeId,
      document_type: document.documentType,
      title: document.title,
      issue_date: issueDate,
      expiry_date: expiryDate,
      status: document.status,
      notifications_sent: document.notificationsSent || [],
      document_url: document.documentUrl || null,
      notes: document.notes || null
    };
    
    console.log('[HR Service] Formatted document data for backend:', backendDocument);
    
    return this.http.post<Document>(`${this.apiUrl}/documents`, backendDocument).pipe(
      tap(newDoc => {
        console.log('[HR Service] Document added successfully:', newDoc);
        const currentDocs = this.documentSubject.value;
        this.documentSubject.next([...currentDocs, newDoc]);
      }),
      catchError(error => {
        console.error('[HR Service] Error adding document:', error);
        console.error('[HR Service] Error details:', error.error);
        if (error.status === 400) {
          console.error('[HR Service] Bad request - validation error in document data');
        }
        return throwError(() => error);
      })
    );
  }

  updateDocument(document: Document): Observable<Document> {
    console.log('[HR Service] Updating document', document);
    
    // Format dates in the correct format (YYYY-MM-DD) for the backend
    let issueDate: string | null = null;
    let expiryDate: string | null = null;
    
    // Format issue date (if present)
    if (document.issueDate) {
      if (document.issueDate instanceof Date) {
        issueDate = document.issueDate.toISOString().split('T')[0];
      } else {
        // If it's a string or some other format, try to convert it
        const issueDateObj = new Date(document.issueDate as any);
        if (!isNaN(issueDateObj.getTime())) {
          issueDate = issueDateObj.toISOString().split('T')[0];
        }
      }
    }
    
    // Format expiry date (if present)
    if (document.expiryDate) {
      if (document.expiryDate instanceof Date) {
        expiryDate = document.expiryDate.toISOString().split('T')[0];
      } else {
        // If it's a string or some other format, try to convert it
        const expiryDateObj = new Date(document.expiryDate as any);
        if (!isNaN(expiryDateObj.getTime())) {
          expiryDate = expiryDateObj.toISOString().split('T')[0];
        }
      }
    }
    
    console.log('[HR Service] Formatted issue date:', issueDate);
    console.log('[HR Service] Formatted expiry date:', expiryDate);
    
    // Convert to backend format with snake_case
    const backendDocument = {
      employee_id: document.employeeId,
      document_type: document.documentType,
      title: document.title,
      issue_date: issueDate,
      expiry_date: expiryDate,
      status: document.status,
      notifications_sent: document.notificationsSent || [],
      document_url: document.documentUrl || null,
      notes: document.notes || null
    };
    
    console.log('[HR Service] Formatted document data for backend:', backendDocument);
    
    return this.http.put<Document>(`${this.apiUrl}/documents/${document.id}`, backendDocument).pipe(
      tap(updatedDoc => {
        console.log('[HR Service] Document updated successfully:', updatedDoc);
        const currentDocs = this.documentSubject.value;
        const updatedDocs = currentDocs.map(doc =>
          doc.id === updatedDoc.id ? updatedDoc : doc
        );
        this.documentSubject.next(updatedDocs);
      }),
      catchError(error => {
        console.error('[HR Service] Error updating document:', error);
        console.error('[HR Service] Error details:', error.error);
        if (error.status === 400) {
          console.error('[HR Service] Bad request - validation error in document data');
        }
        return throwError(() => error);
      })
    );
  }

  deleteDocument(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/documents/${id}`).pipe(
      tap(() => {
        const currentDocs = this.documentSubject.value;
        this.documentSubject.next(currentDocs.filter(doc => doc.id !== id));
      }),
      catchError(this.handleError<void>('deleteDocument'))
    );
  }

  // CV Upload and Analysis Methods
  uploadCVDocument(formData: FormData): Observable<Document> {
    console.log('[HR Service] Uploading CV document');
    
    // Add debug information to see what's in the formData
    try {
      // Log form data contents (for debugging)
      console.log('[HR Service] Form data keys:');
      // Use this approach to avoid the TypeScript error with FormData.entries()
      formData.forEach((value, key) => {
        if (key === 'cv_file' && value instanceof Blob) {
          console.log(`[HR Service] ${key}: file size ${value.size}, type: ${value.type}`);
        } else {
          console.log(`[HR Service] ${key}: ${value}`);
        }
      });
      
      // Verify that all required fields are present
      const requiredFields = ['employeeId', 'title', 'issueDate', 'cv_file'];
      const missingFields = requiredFields.filter(field => !formData.has(field));
      
      if (missingFields.length > 0) {
        console.error(`[HR Service] Missing required fields in FormData: ${missingFields.join(', ')}`);
        console.error('[HR Service] This will cause the upload to fail');
      }
    } catch (e) {
      console.error('[HR Service] Error inspecting form data:', e);
    }
    
    // Log the URL we're calling
    console.log(`[HR Service] Calling API endpoint: ${this.apiUrl}/cv/upload`);
    
    return this.http.post<Document>(`${this.apiUrl}/cv/upload`, formData).pipe(
      tap(newDoc => {
        console.log('[HR Service] CV document uploaded successfully:', newDoc);
        const currentDocs = this.documentSubject.value;
        this.documentSubject.next([...currentDocs, newDoc]);
      }),
      catchError(error => {
        console.error('[HR Service] Error uploading CV document:', error);
        
        // Add more detailed error logging
        if (error.error) {
          console.error('[HR Service] Error details:', error.error);
          console.error('[HR Service] Error message:', error.error.message);
        }
        
        if (error.status === 400) {
          console.error('[HR Service] Bad request - validation error in form data');
          console.error('[HR Service] Make sure all required fields are present: employeeId, title, issueDate, cv_file');
        } else if (error.status === 401) {
          console.error('[HR Service] Authentication error - token may be invalid or expired');
        } else if (error.status === 413) {
          console.error('[HR Service] File too large - the CV file may exceed the size limit (10MB)');
        } else if (error.status === 415) {
          console.error('[HR Service] Unsupported media type - the CV file format may not be supported (only PDF and DOCX allowed)');
        } else if (error.status === 404) {
          console.error('[HR Service] API endpoint not found - check if the CV API is properly configured');
        } else if (error.status === 500) {
          console.error('[HR Service] Server error - check the backend logs for more details');
        }
        
        return throwError(() => error);
      })
    );
  }

  // Document Upload Method
  uploadDocument(formData: FormData): Observable<Document> {
    console.log('[HR Service] Uploading document with file');
    
    // Debug information
    try {
      console.log('[HR Service] Document upload form data keys:');
      formData.forEach((value, key) => {
        if (key === 'document_file' && value instanceof Blob) {
          console.log(`[HR Service] ${key}: file size ${value.size}, type: ${value.type}`);
        } else {
          console.log(`[HR Service] ${key}: ${value}`);
        }
      });
      
      // Verify required fields in snake_case format (backend format)
      const requiredFields = ['employee_id', 'title', 'document_type', 'issue_date', 'expiry_date', 'document_file'];
      const missingFields = requiredFields.filter(field => !formData.has(field));
      
      if (missingFields.length > 0) {
        console.error(`[HR Service] Missing required fields in FormData: ${missingFields.join(', ')}`);
        console.error('[HR Service] This will cause the upload to fail');
      }
    } catch (e) {
      console.error('[HR Service] Error inspecting document form data:', e);
    }
    
    return this.http.post<any>(`${this.apiUrl}/documents/upload`, formData).pipe(
      map(doc => ({
        id: doc.id,
        employeeId: doc.employee_id,
        documentType: doc.document_type,
        title: doc.title,
        issueDate: new Date(doc.issue_date),
        expiryDate: new Date(doc.expiry_date),
        status: doc.status,
        notificationsSent: doc.notifications_sent || [],
        documentUrl: doc.document_url,
        notes: doc.notes,
        cv_summary: doc.cv_summary,
        cv_analysis_status: doc.cv_analysis_status,
        cv_analysis_date: doc.cv_analysis_date ? new Date(doc.cv_analysis_date) : undefined,
        file_name: doc.file_name,
        file_path: doc.file_path,
        file_type: doc.file_type,
        file_size: doc.file_size
      })),
      tap(newDoc => {
        console.log('[HR Service] Document uploaded successfully:', newDoc);
        const currentDocs = this.documentSubject.value;
        this.documentSubject.next([...currentDocs, newDoc]);
      }),
      catchError(error => {
        console.error('[HR Service] Error uploading document:', error);
        
        if (error.error) {
          console.error('[HR Service] Error details:', error.error);
          console.error('[HR Service] Error message:', error.error.message);
        }
        
        if (error.status === 400) {
          console.error('[HR Service] Bad request - validation error in form data');
          console.error('[HR Service] Make sure all required fields are present');
        } else if (error.status === 401) {
          console.error('[HR Service] Authentication error - token may be invalid or expired');
        } else if (error.status === 413) {
          console.error('[HR Service] File too large - the document file may exceed the size limit (10MB)');
        } else if (error.status === 415) {
          console.error('[HR Service] Unsupported media type - the document file format may not be supported (only PDF and DOCX allowed)');
        } else if (error.status === 404) {
          console.error('[HR Service] API endpoint not found - check if the document upload API is properly configured');
        } else if (error.status === 500) {
          console.error('[HR Service] Server error - check the backend logs for more details');
        }
        
        return throwError(() => error);
      })
    );
  }

  // Multiple Document Upload Method
  uploadMultipleDocuments(formData: FormData): Observable<any> {
    console.log('[HR Service] Uploading multiple documents with files');
    
    // Debug information
    try {
      console.log('[HR Service] Multiple document upload form data keys:');
      formData.forEach((value, key) => {
        if (key === 'document_files' && value instanceof File) {
          console.log(`[HR Service] ${key}: file size ${(value as File).size}, type: ${(value as File).type}`);
        } else {
          console.log(`[HR Service] ${key}: ${value}`);
        }
      });
      
      // Verify required fields
      const requiredFields = ['employeeId', 'documentType', 'issueDate', 'expiryDate'];
      const missingFields = requiredFields.filter(field => !formData.has(field));
      
      if (missingFields.length > 0) {
        console.error(`[HR Service] Missing required fields in FormData: ${missingFields.join(', ')}`);
      }
    } catch (e) {
      console.error('[HR Service] Error inspecting multiple document form data:', e);
    }
    
    // Log the URL we're calling
    console.log(`[HR Service] Calling API endpoint: ${this.apiUrl}/documents/upload`);
    
    // Use the existing /documents/upload endpoint with isMultiple flag
    // Pass isMultiple as a URL parameter instead of form data
    return this.http.post<any>(`${this.apiUrl}/documents/upload?isMultiple=true`, formData).pipe(
      tap(response => {
        console.log('[HR Service] Multiple documents uploaded successfully:', response);
        if (response.documents && response.documents.length > 0) {
          const currentDocs = this.documentSubject.value;
          this.documentSubject.next([...currentDocs, ...response.documents]);
        }
      }),
      catchError(error => {
        console.error('[HR Service] Error uploading multiple documents:', error);
        
        if (error.error) {
          console.error('[HR Service] Error details:', error.error);
          console.error('[HR Service] Error message:', error.error.message);
        }
        
        if (error.status === 400) {
          console.error('[HR Service] Bad request - validation error in form data');
        } else if (error.status === 401) {
          console.error('[HR Service] Authentication error - token may be invalid or expired');
        } else if (error.status === 413) {
          console.error('[HR Service] File too large - some document files may exceed the size limit (10MB)');
        } else if (error.status === 415) {
          console.error('[HR Service] Unsupported media type - some document file formats may not be supported');
        }
        
        return throwError(() => error);
      })
    );
  }

  getCVAnalysis(documentId: number): Observable<any> {
    console.log(`[HR Service] Getting CV analysis for document ID: ${documentId}`);
    return this.http.get<any>(`${this.apiUrl}/cv/${documentId}/analysis`).pipe(
      tap(analysis => console.log('[HR Service] Successfully retrieved CV analysis')),
      catchError(error => {
        console.error('[HR Service] Error getting CV analysis:', error);
        return throwError(() => error);
      })
    );
  }

  getEmployeeCVs(employeeId: number): Observable<Document[]> {
    console.log(`[HR Service] Getting CVs for employee ID: ${employeeId}`);
    return this.http.get<Document[]>(`${this.apiUrl}/cv/employee/${employeeId}`).pipe(
      tap(docs => console.log(`[HR Service] Retrieved ${docs.length} CV documents for employee`)),
      catchError(error => {
        console.error('[HR Service] Error getting employee CVs:', error);
        return throwError(() => error);
      })
    );
  }

  reanalyzeCVDocument(documentId: number): Observable<any> {
    console.log(`[HR Service] Reanalyzing CV document ID: ${documentId}`);
    return this.http.post<any>(`${this.apiUrl}/cv/${documentId}/reanalyze`, {}).pipe(
      tap(result => console.log('[HR Service] Successfully reanalyzed CV document')),
      catchError(error => {
        console.error('[HR Service] Error reanalyzing CV document:', error);
        console.error('[HR Service] Error details:', error.error);
        return throwError(() => error);
      })
    );
  }

  // Employee methods
  getEmployees(): Observable<Employee[]> {
    return this.http.get<any[]>(`${this.apiUrl}/employees`).pipe(
      map(employees => employees.map(employee => {
        // Convert from potential snake_case fields and ensure dates are properly handled
        return {
          id: employee.id,
          name: employee.name,
          email: employee.email,
          phone: employee.phone || employee.phone_number,
          department: employee.department,
          position: employee.position || employee.job_title,
          hireDate: employee.hireDate ? new Date(employee.hireDate) : 
                   employee.hire_date ? new Date(employee.hire_date) : 
                   undefined,
          birthDate: employee.birthDate ? new Date(employee.birthDate) : 
                     employee.birth_date ? new Date(employee.birth_date) : 
                     undefined,
          workplace: employee.workplace,
          skills: employee.skills
        } as Employee;
      })),
      tap(employees => this.employeeSubject.next(employees)),
      catchError(this.handleError<Employee[]>('getEmployees', []))
    );
  }

  getEmployeeById(id: number): Observable<Employee> {
    return this.http.get<Employee>(`${this.apiUrl}/employees/${id}`).pipe(
      catchError(this.handleError<Employee>(`getEmployee id=${id}`))
    );
  }

  addEmployee(employee: Employee): Observable<Employee> {
    console.log('[HR Service] Adding employee:', employee);
    console.log('[HR Service] Using API URL:', `${this.apiUrl}/employees`);
    
    // Format dates in the correct format (YYYY-MM-DD) for the backend
    let hireDate: string | null = null;
    let birthDate: string | null = null;
    
    // Format hire date (if present)
    if (employee.hireDate) {
      // Handle different date formats
      if (employee.hireDate instanceof Date) {
        hireDate = employee.hireDate.toISOString().split('T')[0];
      } else {
        // If it's a string or some other format, try to convert it
        const hireDateObj = new Date(employee.hireDate as any);
        if (!isNaN(hireDateObj.getTime())) {
          hireDate = hireDateObj.toISOString().split('T')[0];
        }
      }
    }
    
    // Format birth date (if present)
    if (employee.birthDate) {
      // Handle different date formats
      if (employee.birthDate instanceof Date) {
        birthDate = employee.birthDate.toISOString().split('T')[0];
      } else {
        // If it's a string or some other format, try to convert it
        const birthDateObj = new Date(employee.birthDate as any);
        if (!isNaN(birthDateObj.getTime())) {
          birthDate = birthDateObj.toISOString().split('T')[0];
        }
      }
    }
    
    console.log('[HR Service] Formatted hire date:', hireDate);
    console.log('[HR Service] Formatted birth date:', birthDate);
    
    // Convert the employee object to match backend field names with snake_case
    const backendEmployee = {
      name: employee.name,
      email: employee.email,
      phone: employee.phone || null,
      department: employee.department || null,
      position: employee.position || null,
      hire_date: hireDate, // Use snake_case for backend
      birth_date: birthDate, // Use snake_case for backend
      workplace: employee.workplace || null,
      skills: employee.skills || null
    };
    
    console.log('[HR Service] Formatted employee data for backend:', backendEmployee);
    
    return this.http.post<Employee>(`${this.apiUrl}/employees`, backendEmployee).pipe(
      tap(newEmployee => {
        console.log('[HR Service] Employee added successfully:', newEmployee);
        const currentEmployees = this.employeeSubject.value;
        this.employeeSubject.next([...currentEmployees, newEmployee]);
      }),
      catchError(error => {
        console.error('[HR Service] Error adding employee:', error);
        console.error('[HR Service] Error details:', error.error);
        if (error.status === 401) {
          console.error('[HR Service] Authentication error - token may be invalid or expired');
        } else if (error.status === 400) {
          console.error('[HR Service] Bad request - validation error in employee data');
        }
        return throwError(() => error);
      })
    );
  }

  updateEmployee(employee: Employee): Observable<Employee> {
    console.log('[HR Service] Updating employee:', employee);
    
    // Format dates in the correct format (YYYY-MM-DD) for the backend
    let hireDate: string | null = null;
    let birthDate: string | null = null;
    
    // Format hire date (if present)
    if (employee.hireDate) {
      // Handle different date formats
      if (employee.hireDate instanceof Date) {
        hireDate = employee.hireDate.toISOString().split('T')[0];
      } else {
        // If it's a string or some other format, try to convert it
        const hireDateObj = new Date(employee.hireDate as any);
        if (!isNaN(hireDateObj.getTime())) {
          hireDate = hireDateObj.toISOString().split('T')[0];
        }
      }
    }
    
    // Format birth date (if present)
    if (employee.birthDate) {
      // Handle different date formats
      if (employee.birthDate instanceof Date) {
        birthDate = employee.birthDate.toISOString().split('T')[0];
      } else {
        // If it's a string or some other format, try to convert it
        const birthDateObj = new Date(employee.birthDate as any);
        if (!isNaN(birthDateObj.getTime())) {
          birthDate = birthDateObj.toISOString().split('T')[0];
        }
      }
    }
    
    console.log('[HR Service] Formatted hire date:', hireDate);
    console.log('[HR Service] Formatted birth date:', birthDate);
    
    // Convert the employee object to match backend field names with snake_case
    const backendEmployee = {
      name: employee.name,
      email: employee.email,
      phone: employee.phone || null,
      department: employee.department || null,
      position: employee.position || null,
      hire_date: hireDate, // Use snake_case for backend
      birth_date: birthDate, // Use snake_case for backend
      workplace: employee.workplace || null,
      skills: employee.skills || null
    };
    
    console.log('[HR Service] Formatted employee data for backend:', backendEmployee);

    return this.http.put<Employee>(`${this.apiUrl}/employees/${employee.id}`, backendEmployee).pipe(
      tap(updatedEmployee => {
        console.log('[HR Service] Employee updated successfully:', updatedEmployee);
        const currentEmployees = this.employeeSubject.value;
        const updatedEmployees = currentEmployees.map(emp =>
          emp.id === updatedEmployee.id ? updatedEmployee : emp
        );
        this.employeeSubject.next(updatedEmployees);
      }),
      catchError(error => {
        console.error('[HR Service] Error updating employee:', error);
        console.error('[HR Service] Error details:', error.error);
        if (error.status === 401) {
          console.error('[HR Service] Authentication error - token may be invalid or expired');
        } else if (error.status === 400) {
          console.error('[HR Service] Bad request - validation error in employee data');
        }
        return throwError(() => error);
      })
    );
  }

  deleteEmployee(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/employees/${id}`).pipe(
      tap(() => {
        const currentEmployees = this.employeeSubject.value;
        this.employeeSubject.next(currentEmployees.filter(emp => emp.id !== id));
      }),
      catchError(this.handleError<void>('deleteEmployee'))
    );
  }

  // Personal events methods
  getPersonalEvents(): Observable<PersonalEvent[]> {
    return this.http.get<any[]>(`${this.apiUrl}/personal-events`).pipe(
      map(events => events.map(event => {
        // Convert from snake_case to camelCase
        return {
          id: event.id,
          employeeId: event.employee_id,
          eventType: event.event_type,
          date: new Date(event.event_date),
          notificationSent: event.notification_sent
        } as PersonalEvent;
      })),
      tap(events => this.personalEventsSubject.next(events)),
      catchError(this.handleError<PersonalEvent[]>('getPersonalEvents', []))
    );
  }

  getUpcomingPersonalEvents(daysAhead: number): Observable<PersonalEvent[]> {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(now.getDate() + daysAhead);

    return this.personalEvents$.pipe(
      map(events => events.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= now && eventDate <= futureDate;
      }))
    );
  }

  addPersonalEvent(event: PersonalEvent): Observable<PersonalEvent> {
    console.log('[HR Service] Adding personal event:', event);
    
    // Format the date to YYYY-MM-DD format for the backend
    let formattedDate: string | null = null;
    
    if (event.date) {
      if (event.date instanceof Date) {
        formattedDate = event.date.toISOString().split('T')[0];
      } else {
        // If it's a string or some other format, try to convert it
        const dateObj = new Date(event.date as any);
        if (!isNaN(dateObj.getTime())) {
          formattedDate = dateObj.toISOString().split('T')[0];
        }
      }
    }
    
    console.log('[HR Service] Formatted event date:', formattedDate);
    
    // Convert to backend format with snake_case
    const backendEvent = {
      employee_id: event.employeeId,
      event_type: event.eventType,
      event_date: formattedDate,
      notification_sent: event.notificationSent
    };
    
    console.log('[HR Service] Formatted event data for backend:', backendEvent);
    
    return this.http.post<PersonalEvent>(`${this.apiUrl}/personal-events`, backendEvent).pipe(
      tap(newEvent => {
        console.log('[HR Service] Personal event added successfully:', newEvent);
        const currentEvents = this.personalEventsSubject.value;
        this.personalEventsSubject.next([...currentEvents, newEvent]);
      }),
      catchError(error => {
        console.error('[HR Service] Error adding personal event:', error);
        console.error('[HR Service] Error details:', error.error);
        if (error.status === 400) {
          console.error('[HR Service] Bad request - validation error in event data');
        }
        return throwError(() => error);
      })
    );
  }

  updatePersonalEvent(event: PersonalEvent): Observable<PersonalEvent> {
    console.log('[HR Service] Updating personal event:', event);
    
    // Format the date to YYYY-MM-DD format for the backend
    let formattedDate: string | null = null;
    
    if (event.date) {
      if (event.date instanceof Date) {
        formattedDate = event.date.toISOString().split('T')[0];
      } else {
        // If it's a string or some other format, try to convert it
        const dateObj = new Date(event.date as any);
        if (!isNaN(dateObj.getTime())) {
          formattedDate = dateObj.toISOString().split('T')[0];
        }
      }
    }
    
    console.log('[HR Service] Formatted event date:', formattedDate);
    
    // Convert to backend format with snake_case
    const backendEvent = {
      employee_id: event.employeeId,
      event_type: event.eventType,
      event_date: formattedDate,
      notification_sent: event.notificationSent
    };
    
    console.log('[HR Service] Formatted event data for backend:', backendEvent);
    
    return this.http.put<PersonalEvent>(`${this.apiUrl}/personal-events/${event.id}`, backendEvent).pipe(
      tap(updatedEvent => {
        console.log('[HR Service] Personal event updated successfully:', updatedEvent);
        const currentEvents = this.personalEventsSubject.value;
        const updatedEvents = currentEvents.map(evt =>
          evt.id === updatedEvent.id ? updatedEvent : evt
        );
        this.personalEventsSubject.next(updatedEvents);
      }),
      catchError(error => {
        console.error('[HR Service] Error updating personal event:', error);
        console.error('[HR Service] Error details:', error.error);
        if (error.status === 400) {
          console.error('[HR Service] Bad request - validation error in event data');
        }
        return throwError(() => error);
      })
    );
  }

  deletePersonalEvent(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/personal-events/${id}`).pipe(
      tap(() => {
        const currentEvents = this.personalEventsSubject.value;
        this.personalEventsSubject.next(currentEvents.filter(evt => evt.id !== id));
      }),
      catchError(this.handleError<void>('deletePersonalEvent'))
    );
  }

  // Notification methods
  getNotificationSettings(): Observable<NotificationSettings> {
    // Define default settings
    const defaultSettings: NotificationSettings = {
      documentExpiryReminderDays: [30, 15, 7, 1],
      birthdayReminderDays: 3,
      anniversaryReminderDays: 5,
      defaultNotificationChannel: NotificationChannel.Both,
      hrEmailAddresses: ['hr@company.com']
    };
    
    return this.http.get<NotificationSettings>(`${this.apiUrl}/notification-settings`).pipe(
      // Map the response to ensure all properties exist
      map(settings => {
        return {
          documentExpiryReminderDays: settings.documentExpiryReminderDays || defaultSettings.documentExpiryReminderDays,
          birthdayReminderDays: settings.birthdayReminderDays || defaultSettings.birthdayReminderDays,
          anniversaryReminderDays: settings.anniversaryReminderDays || defaultSettings.anniversaryReminderDays,
          defaultNotificationChannel: settings.defaultNotificationChannel || defaultSettings.defaultNotificationChannel,
          hrEmailAddresses: settings.hrEmailAddresses || defaultSettings.hrEmailAddresses
        };
      }),
      tap(settings => this.notificationSettingsSubject.next(settings)),
      catchError(this.handleError<NotificationSettings>('getNotificationSettings', defaultSettings))
    );
  }

  updateNotificationSettings(settings: NotificationSettings): Observable<NotificationSettings> {
    // Define default settings
    const defaultSettings: NotificationSettings = {
      documentExpiryReminderDays: [30, 15, 7, 1],
      birthdayReminderDays: 3,
      anniversaryReminderDays: 5,
      defaultNotificationChannel: NotificationChannel.Both,
      hrEmailAddresses: ['hr@company.com']
    };
    
    // Ensure all properties exist in the settings being updated
    const completeSettings: NotificationSettings = {
      documentExpiryReminderDays: settings.documentExpiryReminderDays || defaultSettings.documentExpiryReminderDays,
      birthdayReminderDays: settings.birthdayReminderDays || defaultSettings.birthdayReminderDays,
      anniversaryReminderDays: settings.anniversaryReminderDays || defaultSettings.anniversaryReminderDays,
      defaultNotificationChannel: settings.defaultNotificationChannel || defaultSettings.defaultNotificationChannel,
      hrEmailAddresses: settings.hrEmailAddresses || defaultSettings.hrEmailAddresses
    };
    
    return this.http.put<NotificationSettings>(`${this.apiUrl}/notification-settings`, completeSettings).pipe(
      // Also validate the response
      map(updatedSettings => {
        return {
          documentExpiryReminderDays: updatedSettings.documentExpiryReminderDays || defaultSettings.documentExpiryReminderDays,
          birthdayReminderDays: updatedSettings.birthdayReminderDays || defaultSettings.birthdayReminderDays,
          anniversaryReminderDays: updatedSettings.anniversaryReminderDays || defaultSettings.anniversaryReminderDays,
          defaultNotificationChannel: updatedSettings.defaultNotificationChannel || defaultSettings.defaultNotificationChannel,
          hrEmailAddresses: updatedSettings.hrEmailAddresses || defaultSettings.hrEmailAddresses
        };
      }),
      tap(updatedSettings => this.notificationSettingsSubject.next(updatedSettings)),
      catchError(this.handleError<NotificationSettings>('updateNotificationSettings', completeSettings))
    );
  }

  sendNotification(notification: NotificationRecord): Observable<NotificationRecord> {
    return this.http.post<NotificationRecord>(`${this.apiUrl}/notifications`, notification).pipe(
      catchError(this.handleError<NotificationRecord>('sendNotification'))
    );
  }

  // Document status and summary methods
  getExpiringDocuments(daysAhead: number): Observable<Document[]> {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(now.getDate() + daysAhead);

    return this.documents$.pipe(
      map(documents => documents.filter(doc => {
        const expiryDate = new Date(doc.expiryDate);
        return expiryDate >= now && expiryDate <= futureDate;
      }))
    );
  }

  getExpiredDocuments(): Observable<Document[]> {
    const now = new Date();

    return this.documents$.pipe(
      map(documents => documents.filter(doc => {
        const expiryDate = new Date(doc.expiryDate);
        return expiryDate < now;
      }))
    );
  }

  getDocumentSummary(): Observable<DocumentSummary> {
    return this.documents$.pipe(
      map(documents => {
        const now = new Date();
        let validCount = 0;
        let expiringCount = 0;
        let expiredCount = 0;
        let missingCount = 0;

        documents.forEach(doc => {
          const expiryDate = new Date(doc.expiryDate);
          const daysUntilExpiry = Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

          if (doc.status === DocumentStatus.Missing) {
            missingCount++;
          } else if (daysUntilExpiry < 0) {
            expiredCount++;
          } else if (daysUntilExpiry <= 30) {
            expiringCount++;
          } else {
            validCount++;
          }
        });

        return {
          totalDocuments: documents.length,
          validDocuments: validCount,
          expiringDocuments: expiringCount,
          expiredDocuments: expiredCount,
          missingDocuments: missingCount
        };
      })
    );
  }

  // Excel Import/Export methods
  exportUpcomingEventsToExcel(daysAhead: number = 30): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/employees/excel/upcoming-events/${daysAhead}`, {
      responseType: 'blob'
    }).pipe(
      catchError(error => {
        console.error('[HR Service] Error exporting upcoming events:', error);
        return throwError(() => error);
      })
    );
  }

  // Method to import employees from Excel
  importEmployeesFromExcel(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(`${this.apiUrl}/employees/excel/import`, formData).pipe(
      tap(response => {
        // Refresh employees list after successful import
        this.refreshData();
      }),
      catchError(error => {
        console.error('[HR Service] Error importing employees from Excel:', error);
        return throwError(() => error);
      })
    );
  }

  // Method to export employees to Excel
  exportEmployeesToExcel(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/employees/excel/export`, {
      responseType: 'blob'
    }).pipe(
      catchError(error => {
        console.error('[HR Service] Error exporting employees to Excel:', error);
        return throwError(() => error);
      })
    );
  }

  // Sync employee birth dates and hire dates with personal events
  syncEmployeeDateEvents(): Observable<boolean> {
    console.log('[HR Service] Syncing employee dates with personal events');
    
    return this.getEmployees().pipe(
      // First force refresh the employees data
      switchMap(employees => {
        // Then force refresh personal events data
        return this.getPersonalEvents().pipe(
          map(events => ({ employees, events }))
        );
      }),
      // Now work with the fresh data
      map(({ employees, events }) => {
        console.log(`[HR Service] Checking ${employees.length} employees against ${events.length} events`);
        
        const eventsToAdd: PersonalEvent[] = [];
        
        // Process each employee
        employees.forEach(employee => {
          // Check if employee has a birthday event
          const hasBirthdayEvent = events.some(event => {
            const apiEvent = event as any;
            const eventEmployeeId = apiEvent.employeeId || apiEvent.employee_id;
            const eventType = apiEvent.eventType || apiEvent.event_type;
            return eventEmployeeId === employee.id && eventType === PersonalEventType.Birthday;
          });
          
          // If no birthday event but has birthDate, create one
          if (!hasBirthdayEvent && employee.birthDate) {
            console.log(`[HR Service] Creating birthday event for ${employee.name} (ID: ${employee.id})`);
            eventsToAdd.push({
              id: 0, // Will be assigned by backend
              employeeId: employee.id,
              eventType: PersonalEventType.Birthday,
              date: new Date(employee.birthDate),
              notificationSent: false
            });
          }
          
          // Check if employee has a work anniversary event
          const hasAnniversaryEvent = events.some(event => {
            const apiEvent = event as any;
            const eventEmployeeId = apiEvent.employeeId || apiEvent.employee_id;
            const eventType = apiEvent.eventType || apiEvent.event_type;
            return eventEmployeeId === employee.id && eventType === PersonalEventType.WorkAnniversary;
          });
          
          // If no anniversary event but has hireDate, create one
          if (!hasAnniversaryEvent && employee.hireDate) {
            console.log(`[HR Service] Creating work anniversary event for ${employee.name} (ID: ${employee.id})`);
            eventsToAdd.push({
              id: 0, // Will be assigned by backend
              employeeId: employee.id,
              eventType: PersonalEventType.WorkAnniversary,
              date: new Date(employee.hireDate),
              notificationSent: false
            });
          }
        });
        
        // If no events to add, we're done
        if (eventsToAdd.length === 0) {
          return of(true);
        }
        
        console.log(`[HR Service] Adding ${eventsToAdd.length} new events`);
        
        // Add each event in sequence
        return from(eventsToAdd).pipe(
          concatMap(event => this.addPersonalEvent(event)),
          toArray(), // Collect all results
          map(() => true) // Return true when done
        );
      }),
      // Flatten the nested observable
      concatMap(obs => obs),
      catchError(error => {
        console.error('[HR Service] Error syncing employee dates:', error);
        return of(false);
      })
    );
  }

  /**
   * Add a new HR manager
   * @param userData User data with username, email, password, and company_id
   * @returns Observable with the created user
   */
  addHrManager(userData: {
    username: string;
    email: string;
    password: string;
    company_id?: number;
  }): Observable<any> {
    console.log('[HR Service] Adding new HR manager');
    console.log('[HR Service] API URL for user creation:', `${this.apiUrl}/users`);
    
    // HR managers have the specific role of 'hr' (matching the Sequelize model validation)
    const payload = {
      ...userData,
      role: 'hr'
    };
    
    // Log the full payload for debugging
    console.log('[HR Service] User creation payload:', payload);
    
    return this.http.post(`${this.apiUrl}/users`, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    }).pipe(
      tap(response => {
        console.log('[HR Service] Successfully created new HR manager:', response);
      }),
      catchError(error => {
        console.error('[HR Service] Error creating HR manager:', error);
        // Add more detailed error logging
        if (error.error) {
          console.error('[HR Service] Error details:', error.error);
          console.error('[HR Service] Error message:', error.error.message || 'No error message provided');
          
          if (error.error.errors && Array.isArray(error.error.errors)) {
            error.error.errors.forEach((err: any) => {
              console.error(`[HR Service] Validation error: ${err.msg} (${err.param})`);
            });
          }
        }
        if (error.status === 400) {
          console.error('[HR Service] Bad request - validation error in HR manager data');
        } else if (error.status === 401) {
          console.error('[HR Service] Authentication error - token may be invalid or expired');
        } else if (error.status === 403) {
          console.error('[HR Service] Forbidden - you do not have permission to create HR managers');
        }
        return throwError(() => error);
      })
    );
  }

  // Document Type methods
  getDocumentTypes(): Observable<DocumentType[]> {
    return this.http.get<any[]>(`${this.apiUrl}/document-types`).pipe(
      map(types => types.map(type => {
        // Handle Sequelize instances with dataValues
        if (type.dataValues) {
          return {
            id: type.dataValues.id,
            name: type.dataValues.name,
            status: type.dataValues.status
          };
        }
        // Handle plain objects
        return {
          id: type.id,
          name: type.name,
          status: type.status
        };
      })),
      tap(types => this.documentTypesSubject.next(types)),
      catchError(this.handleError<DocumentType[]>('getDocumentTypes', []))
    );
  }

  addDocumentType(type: DocumentType): Observable<DocumentType> {
    return this.http.post<any>(`${this.apiUrl}/document-types`, type).pipe(
      map(newType => {
        // Handle Sequelize instances with dataValues
        if (newType.dataValues) {
          return {
            id: newType.dataValues.id,
            name: newType.dataValues.name,
            status: newType.dataValues.status
          };
        }
        // Handle plain objects
        return {
          id: newType.id,
          name: newType.name,
          status: newType.status
        };
      }),
      tap(newType => {
        const currentTypes = this.documentTypesSubject.value;
        this.documentTypesSubject.next([...currentTypes, newType]);
      }),
      catchError(this.handleError<DocumentType>('addDocumentType'))
    );
  }

  updateDocumentType(type: DocumentType): Observable<DocumentType> {
    return this.http.put<any>(`${this.apiUrl}/document-types/${type.id}`, type).pipe(
      map(updatedType => {
        // Handle Sequelize instances with dataValues
        if (updatedType.dataValues) {
          return {
            id: updatedType.dataValues.id,
            name: updatedType.dataValues.name,
            status: updatedType.dataValues.status
          };
        }
        // Handle plain objects
        return {
          id: updatedType.id,
          name: updatedType.name,
          status: updatedType.status
        };
      }),
      tap(updatedType => {
        const currentTypes = this.documentTypesSubject.value;
        const updatedTypes = currentTypes.map(t => t.id === updatedType.id ? updatedType : t);
        this.documentTypesSubject.next(updatedTypes);
      }),
      catchError(this.handleError<DocumentType>('updateDocumentType'))
    );
  }

  deleteDocumentType(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/document-types/${id}`).pipe(
      tap(() => {
        const currentTypes = this.documentTypesSubject.value;
        this.documentTypesSubject.next(currentTypes.filter(t => t.id !== id));
      }),
      catchError(this.handleError<void>('deleteDocumentType'))
    );
  }

  getDepartments(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/departments`);
  }

  createDepartment(department: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/departments`, department);
  }

  updateDepartment(id: number, department: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/departments/${id}`, department);
  }

  // Error handling
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning an empty result
      return of(result as T);
    };
  }
}
